import React, {
  useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';

import useUser from '../../hooks/useUser';
import {
  preSessionsCreate,
  sessionsChangeClientId,
  sessionsCreate,
  sessionsCreateWithToken,
  sessionsDestroy,
  verifyToken,
} from '../../services/Sessions';

import { STATUS_PENDING } from '../../assets/AppConstants';
import { HTTP_STATUS_6XX } from '../../assets/constants/Http';

import { getDefaultLanguage } from '../../utils/language';

import useLocalStorage from '../../hooks/useLocalStorage';
import { useNavigateSearchPublic } from '../../hooks/useNavigate';

import { acceptLegal, getLegalTemplate } from '../../services/Legal';

import ChangeClient from '../../views/base/ChangeClient';
import VerificationCode from '../../views/public/VerificationCode';
import { TermsAndConditions } from '../../views/public/TermsAndConditions';
import { ActivateWorkspacePage } from '../../views/public/ActivateWorkspace';

import { useAppContext } from '../app';
import { useAnalyticsContext } from '../analytics';

import UserContext from './context';

const UserContextProvider = ({ children }) => {
  const { user, setUser, emptyUser } = useUser();

  const { blockScreen, handleAlert, handleResetStatus } = useAppContext();
  const { resetAnalytics, initAnalytics } = useAnalyticsContext();
  const [workspaceQueue] = useLocalStorage('workspaceQueue', []);
  const navigateSearch = useNavigateSearchPublic();

  const [legalHtml, setLegalHtml] = useState(null);
  const [openWorkspace, setOpenWorkspace] = useState(false);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [openVerification, setOpenVerification] = useState(false);
  const [mfaData, setMfaData] = useState();

  const userIsLogged = () => Boolean(user?.status);
  const getUserStatus = () => user?.status;
  const getUserPlan = () => user?.currentPlan;
  const getUserRole = () => user?.role;

  const logoutUser = async () => {
    setUser(emptyUser);
    resetAnalytics();
    handleResetStatus();
  };

  const handleOpenLegal = async () => {
    blockScreen(true);

    try {
      const template = await getLegalTemplate();
      setLegalHtml(template);
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      blockScreen(false);
    }
  };

  const handlePasswordExpired = (error) => {
    const { token, code } = error.response.data;
    if (code === HTTP_STATUS_6XX.PASSWORD_EXPIRED) {
      navigateSearch(`/reset-password/${token}`, { passwordExpired: true });
    } else {
      handleAlert(error.response.data.message, 'error');
    }
  };

  const handlePreLogin = async (data) => {
    blockScreen(true);
    try {
      const preSession = await preSessionsCreate(data);

      setUser({
        ...user,
        ...preSession,
        realmId: preSession?.external ? preSession?.clientId : null,
      });

      blockScreen(false);
      return preSession;
    } catch (error) {
      handlePasswordExpired(error);
      blockScreen(false);
      return {};
    }
  };

  const handleLogin = (data) => {
    blockScreen(true);
    Promise.all([sessionsCreate(data)]).then(([session]) => {
      const {
        status,
        clientId,
        qrCode,
        needsToken,
        legalAccepted,
      } = session;

      const defaultLanguage = getDefaultLanguage();
      // eslint-disable-next-line no-param-reassign
      session.language ??= defaultLanguage.id;

      if (clientId) {
        setUser({ ...session, openLegal: !legalAccepted });
        initAnalytics(session);
        setOpenWorkspace(status !== STATUS_PENDING && workspaceQueue.length > 0);
        setOpenVerification(status === STATUS_PENDING);
        if (!legalAccepted) {
          handleOpenLegal();
        }
      } else {
        setMfaData({
          qrCode,
          needsToken,
        });
        setOpenClientModal(true);
      }
    }).catch((error) => {
      handlePasswordExpired(error);
    }).finally(() => {
      blockScreen(false);
    });
  };

  const handleLoginWithToken = ({ idToken, refreshToken, token }) => {
    const data = { idToken, refreshToken, token };
    blockScreen(true);
    Promise.all([sessionsCreateWithToken(data, token)]).then(([session]) => {
      const {
        status,
        clientId,
        qrCode,
        needsToken,
        legalAccepted,
      } = session;

      const defaultLanguage = getDefaultLanguage();
      // eslint-disable-next-line no-param-reassign
      session.language ??= defaultLanguage.id;

      if (clientId) {
        setUser({
          ...user,
          ...session,
          external: true,
          authenticated: true,
          openLegal: !legalAccepted,
        });
        initAnalytics({
          ...user,
          ...session,
          external: true,
        });
        setOpenWorkspace(status !== STATUS_PENDING && workspaceQueue.length > 0);
        setOpenVerification(status === STATUS_PENDING);
        if (!legalAccepted) {
          handleOpenLegal();
        }
      } else {
        setMfaData({
          qrCode,
          needsToken,
        });
        setOpenClientModal(true);
      }
    }).catch((error) => {
      handleAlert(error, 'error');
    }).finally(() => {
      blockScreen(false);
    });
  };

  const handleCloseClientModal = async () => {
    setOpenClientModal(false);
  };

  const handleOpenClientModal = () => setOpenClientModal(true);

  const handleChangeClientId = async ({ clientId, clientName }) => {
    blockScreen(true);

    try {
      const session = await sessionsChangeClientId(clientId);

      const finalSession = {
        ...user,
        ...session,
        clientName,
        multiClient: true,
        authenticated: true,
        openLegal: !session.legalAccepted,
      };
      setUser(finalSession);
      initAnalytics(finalSession);
      handleCloseClientModal();

      if (!session.legalAccepted) {
        handleOpenLegal();
      }
    } catch (error) {
      setOpenClientModal(false);
      handlePasswordExpired(error);
    } finally {
      blockScreen(false);
    }
  };

  const handleVerifyTokenAndChangeClientId = async ({
    device,
    trustDevice,
    mfaToken,
  }) => {
    blockScreen(true);

    let valid = { isValid: false, hasClientId: false };

    try {
      const session = await verifyToken({
        token: mfaToken,
        device: trustDevice ? device : undefined,
      });

      valid = { isValid: true, hasClientId: Boolean(session.clientId) };

      if (session.clientId) {
        const isLogged = userIsLogged();

        const finalSession = { ...user, ...session };
        setUser(finalSession);
        initAnalytics(finalSession);
        handleCloseClientModal();

        if (isLogged) {
          window.location.reload();
        }
      }
    } catch (error) {
      if (error.response.data.code === 632) {
        setUser(emptyUser);
        window.location.reload();
      } else {
        handleAlert(error.response.data.message, 'error');
      }
    } finally {
      blockScreen(false);
    }

    return valid;
  };

  useEffect(() => setOpenVerification(user.status === STATUS_PENDING), [user]);

  const handleLogout = async () => {
    await sessionsDestroy();
    logoutUser();
  };

  const handleCloseVerification = () => {
    setOpenVerification(false);
  };

  const handleOpenQr = ({ qrCode }) => {
    setMfaData({
      qrCode,
      needsToken: true,
    });
    setOpenClientModal(true);
  };

  const handleCloseWorkspace = () => setOpenWorkspace(false);

  const handleCloseLegalTemplate = () => {
    setUser(emptyUser);
  };

  const acceptLegalTemplate = async () => {
    blockScreen(true);
    try {
      await acceptLegal();
      handleAlert('Terms and Conditions accepted succefully!', 'success');
      setUser({ ...user, legalAccepted: true, openLegal: false });
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      blockScreen(false);
    }
  };

  const userValue = useMemo(() => ({
    mfaData,
    openClientModal,
    userSession: user,
    setUserSession: setUser,
    userIsLogged,
    getUserStatus,
    getUserPlan,
    getUserRole,
    logoutUser,
    handleLogin,
    handleLoginWithToken,
    handleLogout,
    handleOpenQr,
    handlePreLogin,
    handleChangeClientId,
    handleOpenClientModal,
    handleCloseClientModal,
    handleVerifyTokenAndChangeClientId,
  }), [user, mfaData, openClientModal]);

  if (userIsLogged() && user?.openLegal && legalHtml) {
    return (
      <TermsAndConditions
        openLegal={user?.openLegal}
        legalContent={legalHtml}
        onAccept={acceptLegalTemplate}
        onClose={handleCloseLegalTemplate}
      />
    );
  }

  return (
    <UserContext.Provider value={userValue}>
      {children}

      {openVerification && (
        <VerificationCode
          signUpInfo={user}
          onClose={handleCloseVerification}
        />
      )}

      {openWorkspace && (
        <ActivateWorkspacePage
          onClose={handleCloseWorkspace}
        />
      )}

      {openClientModal && <ChangeClient mode="public" />}

    </UserContext.Provider>
  );
};

UserContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserContextProvider;
